import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const SupplierAuthContext = createContext(undefined);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/supplier`;

export const SupplierAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem('supplierAccessToken');
    sessionStorage.removeItem('supplierRefreshToken');
    setUser(null);
    setOrganization(null);
    setIsAuthenticated(false);
  }, []);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('supplierAccessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          sessionStorage.getItem('supplierRefreshToken')
        ) {
          originalRequest._retry = true;

          try {
            const refreshToken = sessionStorage.getItem('supplierRefreshToken');
            const response = await axios.post(
              `${API}/auth/refresh`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
              }
            );

            const { access_token, refresh_token } = response.data;
            sessionStorage.setItem('supplierAccessToken', access_token);
            sessionStorage.setItem('supplierRefreshToken', refresh_token);

            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return instance(originalRequest);
          } catch (refreshError) {
            logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setOrganization(response.data.organization);
      setIsAuthenticated(true);
    } catch (error) {
      logout();
    }
  }, [api, logout]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    
    if (response.data.mfa_required) {
      return response.data;
    }
    
    const { access_token, refresh_token, organization_status } = response.data;
    sessionStorage.setItem('supplierAccessToken', access_token);
    sessionStorage.setItem('supplierRefreshToken', refresh_token);
    
    await fetchUser();
    return { ...response.data, organization_status };
  }, [api, fetchUser]);

  const verifyMFA = useCallback(async (email, mfaToken, code) => {
    const response = await api.post('/auth/verify-mfa', {
      email,
      mfa_token: mfaToken,
      code
    });
    
    const { access_token, refresh_token } = response.data;
    sessionStorage.setItem('supplierAccessToken', access_token);
    sessionStorage.setItem('supplierRefreshToken', refresh_token);
    
    await fetchUser();
    return response.data;
  }, [api, fetchUser]);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API}/register`, data);
    return response.data;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = sessionStorage.getItem('supplierAccessToken');
      if (token) {
        await fetchUser();
      }
      setLoading(false);
    };

    initAuth();
  }, [fetchUser]);

  const value = useMemo(
    () => ({
      user,
      organization,
      loading,
      isAuthenticated,
      login,
      verifyMFA,
      register,
      logout,
      api,
    }),
    [user, organization, loading, isAuthenticated, login, verifyMFA, register, logout, api]
  );

  return <SupplierAuthContext.Provider value={value}>{children}</SupplierAuthContext.Provider>;
};

export const useSupplierAuth = () => {
  const context = useContext(SupplierAuthContext);
  if (!context) {
    throw new Error('useSupplierAuth must be used within SupplierAuthProvider');
  }
  return context;
};
